import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MdMarkEmailRead } from "react-icons/md";
import { FiArrowLeft } from "react-icons/fi";
import { useGoogleLogin } from "@react-oauth/google";
import { loginApi, googleLoginApi } from "./api";
import AuthCard from "../../components/auth/AuthCard";
import AuthBrand from "../../components/auth/AuthBrand";
import AuthHeaderLink from "../../components/auth/AuthHeaderLink";
import InputField from "../../components/auth/InputField";
import PasswordField from "../../components/auth/PasswordField";
import SocialButtons from "../../components/auth/SocialButtons";
import OtpInput from "../../components/auth/OtpInput";

const RESEND_SECONDS = 30;
const OTP_LENGTH = 6;

const maskEmail = (email = "") => {
  const [name, domain] = email.split("@");
  if (!domain) return email;
  const visible = name.slice(0, name.length > 2 ? 2 : 1);
  return `${visible}${"•".repeat(Math.max(name.length - visible.length, 2))}@${domain}`;
};

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "", rememberMe: false });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();

  // On a successful login we do a full reload (navigate(0)) so every provider
  // re-bootstraps with the new session. This flag tells the auth bootstrap that
  // followed the reload to keep showing "Preparing your workspace…" until the
  // dashboard is ready — so the user never sees a blank screen in between.
  const completeLogin = () => {
    sessionStorage.setItem("sb_just_logged_in", "1");
    navigate(0);
  };

  const loginWithGoogle = useGoogleLogin({
    onSuccess: async ({ access_token }) => {
      setGoogleLoading(true);
      setServerError("");
      try {
        const res = await googleLoginApi(access_token);
        if (res.status) {
          completeLogin();
        } else {
          setServerError(res.message || "Google login failed. Please try again.");
        }
      } catch (err) {
        setServerError(err?.response?.data?.message || "Google login failed. Please try again.");
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: () => setServerError("Google sign-in was cancelled or failed. Please try again."),
  });

  // ── OTP verification step ──
  const [step, setStep] = useState("login"); // "login" | "otp"
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpInfo, setOtpInfo] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const timerRef = useRef(null);

  const startResendTimer = useCallback(() => {
    setResendIn(RESEND_SECONDS);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendIn((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const validate = () => {
    const errs = {};
    if (!form.email) {
      errs.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = "Enter a valid work email address.";
    }
    if (!form.password) errs.password = "Password is required.";
    return errs;
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) return setErrors(errs);
    setServerError("");
    setIsLoading(true);
    try {
      const res = await loginApi({ email: form.email.trim().toLowerCase(), password: form.password });
      if (res.status) {
        completeLogin();
      } else if (res.require_otp_verification) {
        // Email not verified yet — move to OTP step
        setOtp("");
        setOtpError("");
        setOtpInfo(res.message || "We've sent a verification code to your email.");
        setStep("otp");
        startResendTimer();
      } else {
        setServerError(res.message || "Invalid email or password. Please try again.");
      }
    } catch (err) {
      setServerError(
        err?.response?.data?.message || "Invalid email or password. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e?.preventDefault();
    if (otp.length !== OTP_LENGTH) {
      setOtpError(`Enter the ${OTP_LENGTH}-digit code.`);
      return;
    }
    setOtpError("");
    setVerifying(true);
    try {
      const res = await loginApi({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        verification_otp: Number(otp),
      });
      if (res.status) {
        completeLogin();
      } else {
        setOtpError(res.message || "Invalid or expired code. Please try again.");
      }
    } catch (err) {
      setOtpError(
        err?.response?.data?.message || "Invalid or expired code. Please try again."
      );
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || resending) return;
    setResending(true);
    setOtpError("");
    setOtpInfo("");
    try {
      const res = await loginApi({ email: form.email.trim().toLowerCase(), password: form.password });
      if (res.require_otp_verification || res.otp_sent) {
        setOtp("");
        setOtpInfo(res.message || "A new verification code has been sent.");
        startResendTimer();
      } else if (res.status) {
        completeLogin();
      } else {
        setOtpError(res.message || "Couldn't resend the code. Please try again.");
      }
    } catch (err) {
      setOtpError(err?.response?.data?.message || "Couldn't resend the code. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const handleBack = () => {
    clearInterval(timerRef.current);
    setStep("login");
    setOtp("");
    setOtpError("");
    setOtpInfo("");
    setResendIn(0);
  };

  if (step === "otp") {
    return (
      <AuthCard
        headerRight={<AuthHeaderLink prompt="New to SwiftBoard?" to="/register" label="Create an account" />}
      >
        {/* Back */}
        <button
          onClick={handleBack}
          className="flex items-center gap-1.5 text-[13px] text-text_muted mb-6"
        >
          <FiArrowLeft size={15} /> Back to login
        </button>

        {/* Icon + Heading */}
        <div className="mb-6">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
            <MdMarkEmailRead size={24} className="text-primary" />
          </div>
          <h1 className="text-2xl font-semibold text-text_primary tracking-tight leading-tight">
            Verify your email
          </h1>
          <p className="text-sm text-text_muted mt-1.5 leading-relaxed">
            Enter the {OTP_LENGTH}-digit code we sent to{" "}
            <span className="font-medium text-text_secondary">{maskEmail(form.email)}</span>
          </p>
        </div>

        <form onSubmit={handleVerify} className="flex flex-col gap-4">
          <OtpInput
            length={OTP_LENGTH}
            value={otp}
            onChange={(v) => {
              setOtp(v);
              if (otpError) setOtpError("");
            }}
            error={otpError}
            disabled={verifying}
            autoFocus
          />

          {/* Info message */}
          <AnimatePresence>
            {otpInfo && !otpError && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.18 }}
                className="overflow-hidden"
              >
                <div className="rounded bg-success_bg border border-success_border px-3.5 py-2.5 text-[13px] text-success">
                  {otpInfo}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Verify */}
          <button
            type="submit"
            disabled={verifying || otp.length !== OTP_LENGTH}
            className="w-full flex items-center justify-center gap-2 bg-primary text-white text-[13.5px] font-semibold py-2.5 rounded disabled:opacity-55 disabled:cursor-not-allowed"
          >
            {verifying ? (
              <div className="w-[18px] h-[18px] border-2 border-white/25 border-t-white rounded-full animate-spin" />
            ) : (
              "Verify & Continue"
            )}
          </button>

          {/* Resend */}
          <div className="text-center text-[13px] text-text_muted">
            Didn't receive the code?{" "}
            {resendIn > 0 ? (
              <span className="text-text_secondary">
                Resend in <span className="font-semibold text-text_primary">{resendIn}s</span>
              </span>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="text-primary font-semibold hover:underline underline-offset-2 disabled:opacity-55"
              >
                {resending ? "Sending…" : "Resend code"}
              </button>
            )}
          </div>
        </form>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      headerRight={<AuthHeaderLink prompt="New to SwiftBoard?" to="/register" label="Create an account" />}
    >
      {/* Brand — only on small screens, where the brand panel is hidden */}
      <div className="lg:hidden">
        <AuthBrand />
      </div>

      {/* Heading */}
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-semibold text-text_primary tracking-tight leading-tight">
          Welcome back to SwiftBoard
        </h1>
        <div className="flex items-center gap-3 mt-3">
          <div className="flex-1 h-px bg-border" />
          <p className="text-[12.5px] text-text_muted whitespace-nowrap">
            Sign in to your workspace to continue
          </p>
          <div className="flex-1 h-px bg-border" />
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <InputField
          id="email"
          label="Work Email"
          type="email"
          value={form.email}
          onChange={handleChange("email")}
          placeholder="Enter your work email address"
          error={errors.email}
          autoComplete="email"
        />

        <PasswordField
          id="password"
          label="Password"
          value={form.password}
          onChange={handleChange("password")}
          placeholder="Enter your password"
          error={errors.password}
          autoComplete="current-password"
          labelAction={
            <Link
              to="/forgot-password"
              className="text-[12px] font-medium text-primary hover:underline underline-offset-2"
            >
              Forgot password?
            </Link>
          }
        />

        {/* Remember me */}
        <label className="flex items-center gap-2.5 cursor-pointer select-none w-fit">
          <input
            type="checkbox"
            checked={form.rememberMe}
            onChange={handleChange("rememberMe")}
            className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
          />
          <span className="text-[12.5px] text-text_secondary">Keep me signed in for 30 days</span>
        </label>

        {/* Server error */}
        <AnimatePresence>
          {serverError && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.18 }}
              className="overflow-hidden"
            >
              <div className="rounded bg-danger_bg border border-danger_border px-3.5 py-2.5 text-[13px] text-danger">
                {serverError}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 bg-primary text-white text-[13.5px] font-semibold py-2.5 rounded disabled:opacity-55 disabled:cursor-not-allowed mt-1"
        >
          {isLoading ? (
            <div className="w-[18px] h-[18px] border-2 border-white/25 border-t-white rounded-full animate-spin" />
          ) : (
            "Sign in"
          )}
        </button>
      </form>

      <div className="mt-6">
        <SocialButtons onGoogleLogin={loginWithGoogle} googleLoading={googleLoading} />
      </div>
    </AuthCard>
  );
}
